/**!
 * Created by Leonardo Cheslacki on 29/01/2024.
 *
 * MIT License
 *
 * Copyright (c) 2024 cheslacki
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.

 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import PropTypes from 'prop-types';

import SupportController from './SupportController';

import WithType from './../helpers/WithType';

let _this = {};

/**
 *
 */
class SupportTouch extends SupportController {

    /**
     *
     * @param props
     * @param main
     */
    constructor(props, main) {
        super(props);
        this.props = props;
        this.main = main;

        this.$isMultiTouch = false;
        this.$multiTouchStartPos = null;
        this.$eventTarget = null;
        this.$touchElements = {};

        this.init();

        _this = this;
    };

    /**
     *
     * @type {{onMouse: function(*): void}}
     */
    $functions = {
        /**
         * Create a Touch point.
         * @param target
         * @param identifier
         * @param event
         * @param deltaX
         * @param deltaY
         * @constructor
         */
        Touch: function (target, identifier, event, deltaX, deltaY) {
            deltaX = (deltaX || 0);
            deltaY = (deltaY || 0);

            this.target = target;
            this.identifier = identifier;
            this.clientX = (event.clientX + deltaX);
            this.clientY = (event.clientY + deltaY);
            this.pageX = (event.pageX + deltaX);
            this.pageY = (event.pageY + deltaY);
            this.screenX = (event.screenX + deltaX);
            this.screenY = (event.screenY + deltaY);
        },

        /**
         * Create empty touchList with the methods.
         * @returns {*[]}
         * @constructor
         */
        TouchList: function () {
            let array = [];

            /**
             *
             * @param index
             * @returns {*|null}
             */
            array.item = (index) => {
                return (this[index] || null);
            };

            /**
             * Specified by Mozilla.
             * @param id
             * @returns {*|null}
             */
            array.identifiedTouch = (id) => {
                return (this[id + 1] || null);
            };

            return array;
        },

        /**
         *
         * @param event
         * @param type
         */
        handleOnMouse: function (event, {type}) {
            const {ignore_tags, ignore_types} = _this.props;

            if (ignore_tags.indexOf(event.target.tagName) < 0) {
                /* Prevent mouse events */
                _this.stopPropagation(event);
            }

            if (event.which !== 1) {
                return;
            }

            /**
             * The EventTarget on which the touch point started when it was first placed on the surface,
             * even if the touch point has since moved outside the interactive area of that element.
             * also, when the target doesn't exist anymore, we update it
             */
            if ((event.type === 'mousedown') || !_this.$eventTarget || (_this.$eventTarget && !_this.$eventTarget.dispatchEvent)) {
                let composed_path = event.composedPath();

                if (_this.isArrayIf(composed_path)) {
                    _this.$eventTarget = _this.firstArray(composed_path);
                } else {
                    _this.$eventTarget = event.target;
                }
            }

            // shiftKey has been lost, so trigger a touchend
            if (_this.$isMultiTouch && !event.shiftKey) {
                _this.triggerTouch(event, 'touchend');
                _this.$isMultiTouch = false;
            }

            /* We're entering the MultiTouch mode! */
            if (!_this.$isMultiTouch && event.shiftKey) {
                _this.$isMultiTouch = true;
                _this.$multiTouchStartPos = {
                    pageX: event.pageX,
                    pageY: event.pageY,
                    clientX: event.clientX,
                    clientY: event.clientY,
                    screenX: event.screenX,
                    screenY: event.screenY
                };
                _this.triggerTouch(event, 'touchstart');
            }

            _this.triggerTouch(event, type);

            /* Reset */
            if (event.type === 'mouseup') {
                _this.$multiTouchStartPos = null;
                _this.$isMultiTouch = false;
                _this.$eventTarget = null;
            }
        },

        /**
         *
         * @param event
         */
        handleShowTouches: function (event) {
            const {document, template} = _this.props;

            /* First all visible touches */
            for (let i = 0; i < event.touches.length; i++) {
                let
                    touch = event.touches[i],
                    element = _this.$touchElements[touch.identifier],
                    styles = template(touch);

                if (!element) {
                    element = _this.$touchElements[touch.identifier] = document.createElement('DIV');
                    document.body.appendChild(element);
                }

                for (let property in styles) {
                    element.style[property] = styles[property];
                }
            }

            /* Remove all ended touches */
            if (event.type === 'touchend' || event.type === 'touchcancel') {
                for (let i = 0; i < event.changedTouches.length; i++) {
                    let
                        touch = event.changedTouches[i],
                        element = _this.$touchElements[touch.identifier];

                    if (element) {
                        element.parentNode.removeChild(element);
                        delete _this.$touchElements[touch.identifier];
                    }
                }
            }
        }
    };

    /**
     *
     */
    init = () => {
        const {window, document} = this.props;

        /**
         * Polyfills
         */
        if (!document.createTouch) {
            document.createTouch = function (view, target, identifier, pageX, pageY, screenX, screenY, clientX, clientY) {
                /**
                 * Auto set
                 */
                if (clientX === undefined || clientY === undefined) {
                    clientX = (pageX - window.top.pageXOffset);
                    clientY = (pageY - window.top.pageYOffset);
                }

                return (new _this.$functions.Touch(target, identifier, {
                    pageX: pageX,
                    pageY: pageY,
                    screenX: screenX,
                    screenY: screenY,
                    clientX: clientX,
                    clientY: clientY
                }));
            };
        }

        if (!document.createTouchList) {
            document.createTouchList = function () {
                let touchList = (new _this.$functions.TouchList());
                for (let i = 0; i < arguments.length; i++) {
                    touchList[i] = arguments[i];
                }
                touchList.length = arguments.length;
                return touchList;
            };
        }
    };

    /**
     * We don't have to emulate on a touch device.
     * @returns {unknown}
     */
    hasTouchSupport = () => {
        const {window} = this.props;

        return ('ontouchstart' in window.top) || /* Touch events */
            (window.top.Modernizr && window.top.Modernizr.touch) || /* Modernizr */
            ((navigator.msMaxTouchPoints || navigator.maxTouchPoints) > 2); /* Pointer events */
    };

    /**
     * Simple trick to fake touch event support
     * this is enough for most libraries like Modernizr and Hammer.
     */
    fakeTouchSupport = () => {
        const {window, document} = this.props;

        let
            array = [window.top, document.documentElement],
            props = ['ontouchstart', 'ontouchmove', 'ontouchcancel', 'ontouchend'];

        this.recursiveSyncMap(array, 0, array.length, (item_array, nextArray) => {
            this.recursiveSyncMap(props, 0, props.length, (item_props, nextProps) => {
                !this.inObject(item_array, item_props) && (item_array[item_props] = null);
                nextProps(true);
            }, () => {
                nextArray(true)
            });
        });
    }

    /**
     * Create a touchList based on the mouse event.
     * @param event
     * @returns {*[]}
     */
    createTouchList = (event) => {
        const {multi_touch_off_set} = this.props;

        let touchList = (new this.$functions.TouchList());

        if (this.$isMultiTouch) {
            let
                delta_x = (this.$multiTouchStartPos.pageX - event.pageX),
                delta_y = this.$multiTouchStartPos.pageY - event.pageY;

            touchList.push(new this.$functions.Touch(this.$eventTarget, 1, this.$multiTouchStartPos, ((delta_x * -1) - multi_touch_off_set), ((delta_y * -1) + multi_touch_off_set)));
            touchList.push(new this.$functions.Touch(this.$eventTarget, 2, this.$multiTouchStartPos, (delta_x + multi_touch_off_set), (delta_y - multi_touch_off_set)));
        } else {
            touchList.push(new this.$functions.Touch(this.$eventTarget, 1, event, 0, 0));
        }

        return touchList;
    }

    /**
     * Receive all active touches.
     * @param event
     * @param type
     * @returns {*[]}
     */
    getActiveTouches = (event, type) => {
        /* Empty list */
        if (event.type === 'mouseup') {
            return (new this.$functions.TouchList());
        }

        let touch_list = this.createTouchList(event);

        if (this.$isMultiTouch && event.type !== 'mouseup' && type === 'touchend') {
            touch_list.splice(1, 1);
        }

        return touch_list;
    }

    /**
     * Receive a filtered set of touches with only the changed pointers
     * @param event
     * @param type
     * @returns {*[]}
     */
    getChangedTouches = (event, type) => {
        let touch_list = this.createTouchList(event);

        /**
         * We only want to return the added/removed item on MultiTouch
         * which is the second pointer, so remove the first pointer from the touch_list
         *
         * but when the "event.type" is mouseup, we want to send all touches because then
         * no new input will be possible
         */
        if (this.$isMultiTouch && event.type !== 'mouseup' && (type === 'touchstart' || type === 'touchend')) {
            touch_list.splice(0, 1);
        }

        return touch_list;
    }

    /**
     * Trigger a touch event.
     * @param event
     * @param type
     */
    triggerTouch = (event, type) => {
        const {document} = this.props;

        let touchEvent = document.createEvent('Event');

        touchEvent.initEvent(type, true, true);

        touchEvent.altKey = event.altKey;
        touchEvent.ctrlKey = event.ctrlKey;
        touchEvent.metaKey = event.metaKey;
        touchEvent.shiftKey = event.shiftKey;

        touchEvent.touches = this.getActiveTouches(event, type);
        touchEvent.targetTouches = this.getActiveTouches(event, type);
        touchEvent.changedTouches = this.getChangedTouches(event, type);

        this.$eventTarget.dispatchEvent(touchEvent);
    }

    /**
     * Create listeners.
     */
    initTouch = () => {
        const {window} = this.props;

        let types = ['touchstart', 'touchmove', 'touchend', 'touchcancel'];

        // if (this.hasTouchSupport()) {
        //     return;
        // }

        this.fakeTouchSupport();

        this.removeListener('mousedown', 'handle-support-touch-mousedown', () => {
            this.addListener(window, 'mousedown', 'handle-support-touch-mousedown', this.$functions.handleOnMouse, false, {type: 'touchstart'});
        });

        this.removeListener('mousemove', 'handle-support-touch-mousemove', () => {
            this.addListener(window, 'mousemove', 'handle-support-touch-mousemove', this.$functions.handleOnMouse, false, {type: 'touchmove'});
        });

        this.removeListener('mouseup', 'handle-support-touch-mouseup', () => {
            this.addListener(window, 'mouseup', 'handle-support-touch-mouseup', this.$functions.handleOnMouse, false, {type: 'touchend'});
        });

        this.recursiveSyncMap(types, 0, types.length, (item, next) => {
            this.removeListener(item, `handle-support-touch-${item}`, () => {
                this.addListener(window, item, `handle-support-touch-${item}`, this.$functions.handleShowTouches);
            });
            next(true);
        });
    };
}

/**
 *
 * @type {{template: Requireable<(...args: any[]) => any>, ignore_tags: Requireable<any[]>, ignore_types: Requireable<any[]>, window: Requireable<object>, multi_touch_off_set: Requireable<number>}}
 */
SupportTouch.propTypes = {
    window: PropTypes.object,
    document: PropTypes.object,
    template: PropTypes.func,
    ignore_tags: PropTypes.array,
    ignore_types: PropTypes.array,
    multi_touch_off_set: PropTypes.number
};

/**
 *
 * @type {{template: (function(*): {border: string, webkitUserSelect: string, padding: number, margin: number, webkitTransform: string, display: string, userSelect: string, mozTransform: string, mozUserSelect: string, transform: string, overflow: string, top: number, borderRadius: string, left: number, background: string, pointerEvents: string, width: string, position: string, opacity: number, height: string, zIndex: number}), ignore_tags: string[], ignore_types: string[], window: {}, multi_touch_off_set: number}}
 */
SupportTouch.defaultProps = {
    window: {},
    document: {},
    multi_touch_off_set: 75,
    ignore_tags: ['TEXTAREA', 'INPUT', 'SELECT'],
    ignore_types: ['mousedown', 'mouseup'],
    template: (touch) => {
        let
            size = 30,
            transform = `translate(${(touch.clientX - (size / 2))}px, ${(touch.clientY - (size / 2))}px)`;

        return {
            position: 'fixed',
            left: 0,
            top: 0,
            background: '#FFFFFF',
            border: 'solid 1px #999999',
            opacity: 0.3,
            borderRadius: '100%',
            height: `${size}px`,
            width: `${size}px`,
            padding: 0,
            margin: 0,
            display: 'block',
            overflow: 'hidden',
            pointerEvents: 'none',
            webkitUserSelect: 'none',
            mozUserSelect: 'none',
            userSelect: 'none',
            webkitTransform: transform,
            mozTransform: transform,
            transform: transform,
            zIndex: 100
        }
    }
};

/**
 *
 * @param ClassObject
 * @param className {className: 'className'}
 * @returns {function(*): function({}=): *} pass _this ClassMain (required) and props {} (optional)
 */
export default WithType(SupportTouch, {className: 'SupportTouch'});
